import React from 'react';
import {
  Typography,
  Box,
  Grid,
  Button,
  Card,
  CardContent,
  CardActions,
  Divider,
  Chip,
  useTheme,
  Stack,
  CircularProgress
} from '@mui/material';
import {
  Description as FileIcon,
  GetApp as DownloadIcon,
  Code as JsonIcon,
  Visibility as VisibilityIcon,
  Topic as TopicIcon
} from '@mui/icons-material';

interface OutputFilesListProps {
  classificationFiles: { [key: string]: string };
  filesWithContent: { [key: string]: boolean };
  onPreviewFile: (filePath: string, fileName: string) => void;
  onDownloadFile: (filePath: string) => void;
  onPreviewJsonFile?: (filePath: string, fileName: string) => void;
  downloadingFile?: string | null; // Add this prop to track which file is being downloaded
}

const OutputFilesList: React.FC<OutputFilesListProps> = ({
  classificationFiles,
  filesWithContent,
  onPreviewFile,
  onDownloadFile,
  onPreviewJsonFile,
  downloadingFile = null // Default to null if not provided
}) => {
  const theme = useTheme();
  
  if (Object.keys(classificationFiles).length === 0) {
    return null;
  }

  // Helper to determine if a file is JSON
  const isJsonFile = (fileName: string) => {
    return fileName.toLowerCase().endsWith('.json');
  };

  return (
    <Box 
      sx={{ 
        mt: 3, 
        pt: 3, 
        borderTop: '1px solid', 
        borderColor: 'divider'
      }}
    >      
      <Grid container spacing={2}>
        {Object.entries(classificationFiles).map(([fileName, filePath]) => {
          const isTopicFramework = fileName.toLowerCase() === 'topic_framework.json';
          const isDownloading = downloadingFile === filePath;
          let fileIcon = <FileIcon />;
          let fileType = "CSV File";
          
          if (isTopicFramework) {
            fileIcon = <TopicIcon color="primary" />;
            fileType = "Topic Framework";
          } else if (isJsonFile(fileName)) {
            fileIcon = <JsonIcon color="primary" />;
            fileType = "JSON File";
          } else if (fileName.toLowerCase() === 'relevant.csv') {
            fileType = "Relevant Keywords";
          } else if (fileName.toLowerCase() === 'outlier.csv') {
            fileType = "Outlier Keywords";
          } else if (fileName.toLowerCase() === 'undecided.csv') {
            fileType = "Undecided Keywords";
          }
          
          return (
            <Grid item xs={12} sm={6} md={4} key={fileName}>
              <Card variant="outlined" sx={{ 
                height: '100%', 
                mb: { xs: 0, md: 0 },
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  boxShadow: 3,
                  borderColor: theme.palette.primary.main,
                  transform: 'translateY(-4px)'
                }
              }}>
                <CardContent>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: theme.palette.primary.main,
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    {fileIcon}
                    {fileName}
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  
                  <Stack 
                    direction="row" 
                    spacing={2} 
                    alignItems="center" 
                    justifyContent="space-between"
                  >
                    <Chip 
                      label={fileType} 
                      color={isTopicFramework ? "primary" : "default"}
                      variant="outlined"
                      size="small"
                    />
                    
                    {isTopicFramework && (
                      <Typography variant="caption" color="primary.main" sx={{ fontStyle: 'italic' }}>
                        ✨ Enhanced structured view
                      </Typography>
                    )}
                  </Stack>
                </CardContent>
                <CardActions>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => {
                      if (isJsonFile(fileName) && onPreviewJsonFile) {
                        onPreviewJsonFile(filePath, fileName);
                      } else {
                        onPreviewFile(filePath, fileName);
                      }
                    }}
                    disabled={!isJsonFile(fileName) && filesWithContent[filePath] === false}
                    startIcon={<VisibilityIcon />}
                    sx={{ 
                      flex: 1,
                      bgcolor: isTopicFramework ? 'primary.main' : undefined
                    }}
                    color={isTopicFramework ? "primary" : "primary"}
                  >
                    {isTopicFramework ? 'View Topics' : 'Preview'}
                  </Button>
                  
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => onDownloadFile(filePath)}
                    startIcon={isDownloading ? <CircularProgress size={20} /> : <DownloadIcon />}
                    sx={{ flex: 1 }}
                    disabled={isDownloading}
                  >
                    {isDownloading ? 'Downloading...' : 'Download'}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

// Keep the alternative implementation but simplify it to reuse the card component
export const OutputFilesListAlternative: React.FC<OutputFilesListProps> = (props) => {
  // Simply reuse the same card implementation
  return <OutputFilesList {...props} />;
};

export default OutputFilesList;
